'use client';
import { ShieldAlert } from 'lucide-react';
import { type UnifiedMessage } from '@/lib/api';
import { formatCoMessageTime } from '@/app/utils/useFormat';
import { useTranslation } from 'react-i18next';

const REJECT_REASON_KEYS = [
  'PORN',
  'POLITICS',
  'ADS',
  'TERRORISM',
  'ABUSE',
  'DUPLICATE',
  'MISINFORMATION',
  'HARASSMENT',
  'INFRINGEMENT',
  'OTHER',
] as const;

// 消息类型：25 内容审核通知
export function ContentModerationItem({
  message,
  children,
  isDropdown = false,
}: {
  message: UnifiedMessage;
  children?: React.ReactNode;
  isDropdown?: boolean;
}) {
  const { t } = useTranslation();
  const showTimeWithChildren = Boolean(isDropdown && children);
  const rawReason = message.variables?.rejectReasonType as string | undefined;
  const hasReason = rawReason && rawReason !== 'OTHER' && REJECT_REASON_KEYS.includes(rawReason as (typeof REJECT_REASON_KEYS)[number]);
  // 编辑窗口内（≤60min）展示「可编辑」文案，否则展示「可申诉」文案
  const editable = Boolean(message.variables?.editable);
  const reasonLabel = hasReason ? t(`Post.takedown.rejectReasonType.${rawReason}`) : '';
  const descKey = hasReason
    ? editable
      ? 'explore.contentModerationDescEditable'
      : 'explore.contentModerationDescAppealable'
    : editable
      ? 'explore.contentModerationDescEditableNoReason'
      : 'explore.contentModerationDescAppealableNoReason';

  return (
    <>
      <div className="bg-red-500/10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-red-500 border border-red-500/30">
        <ShieldAlert className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        {showTimeWithChildren ? (
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(message.createTime)}</span>
          </div>
        ) : null}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            {!isDropdown && children}
            <h4 className="text-sm font-medium text-font-primary line-clamp-1">{t('explore.contentModerationTitle')}</h4>
          </div>
          {!showTimeWithChildren && (
            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(message.createTime)}</span>
          )}
        </div>
        <p className="text-xs text-font-primary/70 line-clamp-2">{t(descKey, { reason: reasonLabel })}</p>
      </div>
    </>
  );
}
